import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { formatCurrency, getMonthName, getRegimeName } from '../lib/payrollEngine';
import type { Payroll, PayrollItem } from '../lib/supabase';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export function PayrollDetailPage() {
  const { id } = useParams();
  const { company } = useAuth();
  const navigate = useNavigate();
  const [payroll, setPayroll] = useState<Payroll | null>(null);
  const [items, setItems] = useState<PayrollItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);

  useEffect(() => { if (id && company) loadData(); }, [id, company]);

  async function loadData() {
    setLoading(true);
    const [{ data: p }, { data: itms }] = await Promise.all([
      supabase.from('payrolls').select('*').eq('id', id).single(),
      supabase.from('payroll_items')
        .select('*, employee:employees(*)')
        .eq('payroll_id', id)
        .order('created_at'),
    ]);
    setPayroll(p);
    setItems(itms || []);
    setLoading(false);
  }

  // ════════════════════════════════════════════════════════════════════════════
  // GENERADOR DE BOLETA INDIVIDUAL — FORMATO SUNAT PERUANO
  // Replica exactamente el layout de la imagen oficial:
  //   - Dos boletas idénticas por hoja A4 (original + copia)
  //   - 4 secciones: encabezado, datos trabajador, datos laborales,
  //     tabla ingresos/descuentos/aportes, neto a pagar, firmas
  // ════════════════════════════════════════════════════════════════════════════
  function drawBoleta(doc: jsPDF, item: PayrollItem, startX: number, startY: number, bolW: number) {
    const emp = (item as any).employee;

    // Colores
    const BLUE   = [0, 61, 155]    as [number,number,number];
    const GRAY   = [230, 232, 238] as [number,number,number];
    const BLACK  = [0, 0, 0]       as [number,number,number];
    const WHITE  = [255, 255, 255] as [number,number,number];
    const BORDER = [180, 185, 200] as [number,number,number];

    // Nota: calculamos el salud/pensión desde las notas del cálculo
    const notes2       = item.calculation_notes || {};
    const empHealthSys  = String(notes2.healthSystem  || (company?.regime === 'microempresa' ? 'sis' : 'essalud'));
    const empPensionSys = String(notes2.pensionSystem || company?.afp_system || 'onp');
    const healthLabel   = empHealthSys === 'sis' ? 'SIS' : 'ESSALUD';
    const pensionLabel  = empPensionSys === 'afp'
      ? `AFP${emp?.afp_code ? ' ' + emp.afp_code : ''}`
      : empPensionSys === 'ninguno' ? 'SIN APORTE' : 'ONP';

    // ── Helpers ────────────────────────────────────────────────────────────
    // Dibuja una fila etiqueta:valor con anchuras exactas para evitar overflow
    function row(
      lx: number, ly: number,
      label: string, value: string,
      labelW: number, valueW: number,
      fz: number = 6.5
    ) {
      doc.setFontSize(fz);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BLACK);
      // clip label
      const lbl = label.length > 22 ? label.substring(0, 21) + '.' : label;
      doc.text(lbl, lx, ly);
      doc.setFont('helvetica', 'normal');
      // clip value
      const val = value.length > 28 ? value.substring(0, 27) + '.' : value;
      doc.text(val, lx + labelW, ly);
    }

    // Dibuja una fila etiqueta derecha + valor derecho (alineado a la derecha)
    function rowRight(
      lx: number, ly: number,
      label: string, value: string,
      labelW: number, maxX: number,
      fz: number = 6.5
    ) {
      doc.setFontSize(fz);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BLACK);
      const lbl = label.length > 22 ? label.substring(0, 21) + '.' : label;
      doc.text(lbl, lx, ly);
      doc.setFont('helvetica', 'normal');
      doc.text(value, maxX, ly, { align: 'right' });
    }

    let y = startY;

    // ── ENCABEZADO ─────────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...BLACK);
    doc.text(
      `BOLETA DE PAGO - ${getMonthName(payroll!.period_month).toUpperCase()} ${payroll!.period_year}`,
      startX + bolW / 2, y + 5, { align: 'center' }
    );
    y += 8;

    doc.setFontSize(8.5);
    doc.text((company?.name || 'EMPRESA').toUpperCase(), startX + bolW / 2, y + 4, { align: 'center' });
    y += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    const addr = (company?.address || '').substring(0, 60);
    if (addr) { doc.text(addr, startX + bolW / 2, y + 3.5, { align: 'center' }); y += 5; }
    doc.text(`RUC: ${company?.ruc || ''}`, startX + bolW / 2, y + 3.5, { align: 'center' });
    y += 7;

    // ── DATOS DEL TRABAJADOR ──────────────────────────────────────────────
    // Cabecera azul
    doc.setFillColor(...BLUE);
    doc.rect(startX, y, bolW, 6, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...WHITE);
    doc.text('DATOS DEL TRABAJADOR', startX + bolW / 2, y + 4.2, { align: 'center' });
    y += 6;

    // Fondo + borde
    doc.setFillColor(...WHITE);
    doc.setDrawColor(...BORDER);
    doc.rect(startX, y, bolW, 22, 'FD');

    const lW = 34; // ancho etiqueta
    const rH = 5.0;
    const workerRows = [
      ['APELLIDOS Y NOMBRES:', `${(emp?.last_name || '').toUpperCase()}, ${(emp?.first_name || '').toUpperCase()}`],
      ['L.E./DNI:', emp?.dni || ''],
      ['CARGO:', (emp?.position || '').toUpperCase()],
      ['FORMA DE PAGO:', emp?.bank
        ? `DEPÓSITO EN CUENTA ${(emp.bank).toUpperCase()}, CTA: ${emp?.bank_account || '000'}`
        : 'DEPÓSITO EN CUENTA BANCARIA'],
    ];
    workerRows.forEach(([lbl, val], i) => {
      const ry = y + 1.8 + i * rH;
      row(startX + 2, ry + 3, lbl, val, lW, bolW - lW - 4);
      if (i < workerRows.length - 1) {
        doc.setDrawColor(...GRAY);
        doc.line(startX, ry + rH, startX + bolW, ry + rH);
      }
    });
    y += 22;

    // ── DATOS LABORALES ───────────────────────────────────────────────────
    doc.setFillColor(...BLUE);
    doc.rect(startX, y, bolW, 6, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...WHITE);
    doc.text('DATOS LABORALES', startX + bolW / 2, y + 4.2, { align: 'center' });
    y += 6;

    doc.setFillColor(...WHITE);
    doc.setDrawColor(...BORDER);
    doc.rect(startX, y, bolW, 28, 'FD');

    // Columna izquierda (x=startX+2) — ancho etiqueta=30, valor hasta mitad
    const mid = startX + bolW / 2 - 1;
    const lW2 = 29;
    const lW3 = 26;
    const rH2 = 3.7;

    const leftRows: [string, string][] = [
      ['REMUNERACIÓN BÁSICA:', `S/ ${(item.base_salary || 0).toFixed(2)}`],
      ['DÍAS LABORADOS:', String(item.days_worked || 0)],
      ['DÍAS NO LABORADOS:', String(item.absent_days || 0)],
      ['DÍAS SUBSIDIADOS:', ''],
      ['MINUTOS TARDANZA:', String(item.late_minutes_total || 0)],
      [`HORAS EXTRA 25%:`, item.overtime_hours > 0 ? `${item.overtime_hours}h` : '00:00'],
      ['HORAS EXTRA 35%:', '00:00'],
    ];

    leftRows.forEach(([lbl, val], i) => {
      const ry = y + 1.5 + i * rH2;
      doc.setFontSize(6.2);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BLACK);
      doc.text(lbl, startX + 2, ry + 3);
      doc.setFont('helvetica', 'normal');
      doc.text(val, startX + 2 + lW2, ry + 3);
    });

    // Columna derecha (x=mid+2)
    const rightRows: [string, string][] = [
      ['REG. SALUD:', healthLabel],
      ['REG. PENSIONARIO:', pensionLabel],
      ['CUSPP:', emp?.afp_code || (empPensionSys === 'afp' ? (emp?.dni || '') + 'AFP1' : '')],
      ['FECHA INGRESO:', emp?.hire_date || ''],
      ['FECHA CESE:', '/ /'],
      ['VACACIONES:', ''],
    ];

    rightRows.forEach(([lbl, val], i) => {
      const ry = y + 1.5 + i * (28 / 6.2);
      doc.setFontSize(6.2);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BLACK);
      doc.text(lbl, mid + 2, ry + 3);
      doc.setFont('helvetica', 'normal');
      // Truncar valor derecho para que no salga del borde
      const v = val.substring(0, 16);
      doc.text(v, mid + 2 + lW3, ry + 3);
    });

    // Línea divisoria vertical central
    doc.setDrawColor(...GRAY);
    doc.line(mid, y, mid, y + 28);
    y += 28;

    // ── TABLA INGRESOS / DESCUENTOS / APORTES ────────────────────────────
    // Cada columna tiene exactamente bolW/3 mm de ancho
    // Para evitar overflow: etiqueta ocupa 55% de la columna, valor 45%
    const col3   = bolW / 3;             // ancho de cada columna ~32mm
    const txtW   = col3 * 0.56;          // ancho para texto etiqueta
    const numX   = col3 * 0.97;          // posición del número (alineado derecha dentro de la col)

    // Cabeceras
    doc.setFillColor(...GRAY);
    doc.setDrawColor(...BORDER);
    [0, 1, 2].forEach(ci => {
      doc.rect(startX + ci * col3, y, col3, 6, 'FD');
    });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...BLACK);
    doc.text('INGRESOS',    startX + col3 * 0.5,  y + 4.2, { align: 'center' });
    doc.text('DESCUENTOS',  startX + col3 * 1.5,  y + 4.2, { align: 'center' });
    doc.text('APORTES',     startX + col3 * 2.5,  y + 4.2, { align: 'center' });
    y += 6;

    // ── Build data arrays ──
    const ingresos: [string, number][] = [
      ['REMUNERACIÓN BASICA', item.base_salary || 0],
    ];
    if ((item.family_assignment || 0) > 0) ingresos.push(['ASIG. FAMILIAR', item.family_assignment]);
    if ((item.gratification || 0) > 0) ingresos.push(['GRATIFICACIÓN', item.gratification]);
    if ((item.other_income || 0) > 0) ingresos.push(['BONIF. EXTRAORDINARIA', item.other_income]);
    if ((item.overtime_amount || 0) > 0) ingresos.push(['HORAS EXTRA', item.overtime_amount]);
    if ((item.bonuses_total || 0) > 0) ingresos.push(['BONIFICACIONES', item.bonuses_total]);

    const descuentos: [string, number][] = [];
    const afpTotal = item.afp_amount || 0;
    const onpAmt   = item.onp_amount || 0;
    if (empPensionSys === 'afp' && afpTotal > 0) {
      // Desglose AFP: 10% aporte obligatorio + 1.37% prima seguro (total 11.37%)
      const afpPrima  = Math.round(afpTotal * (1.37 / 11.37) * 100) / 100;
      const afpAporte = Math.round((afpTotal - afpPrima) * 100) / 100;
      descuentos.push(['AFP APORTE OBLIG. (10%)', afpAporte]);
      descuentos.push(['AFP PRIMA SEG. (1.37%)',  afpPrima]);
    } else if (onpAmt > 0) {
      descuentos.push(['ONP APORTE OBLIG.', onpAmt]);
    }
    if ((item.income_tax || 0) > 0) descuentos.push(['RENTA 5TA CAT.', item.income_tax]);
    if ((item.absence_deduction || 0) > 0) descuentos.push([`DESC. FALTAS`, item.absence_deduction]);
    if ((item.tardiness_deduction || 0) > 0) descuentos.push(['DESC. TARDANZA', item.tardiness_deduction]);

    const aportes: [string, number][] = [];
    if ((item.essalud_amount || 0) > 0) aportes.push([healthLabel, item.essalud_amount]);

    const maxRows = Math.max(ingresos.length, descuentos.length, aportes.length, 1);
    const rowH3   = 4.6;
    const bodyH   = Math.max(maxRows * rowH3 + 4, 24);

    // Fondo blanco + bordes
    doc.setFillColor(...WHITE);
    doc.setDrawColor(...BORDER);
    doc.rect(startX, y, bolW, bodyH, 'FD');
    // Divisores verticales
    doc.setDrawColor(...GRAY);
    doc.line(startX + col3,     y, startX + col3,     y + bodyH);
    doc.line(startX + col3 * 2, y, startX + col3 * 2, y + bodyH);

    doc.setFontSize(6.5);

    // INGRESOS (col 0)
    ingresos.forEach(([lbl, val], i) => {
      const ry = y + 2 + i * rowH3;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...BLACK);
      // Texto etiqueta — máx txtW mm desde el borde izquierdo de la col
      doc.text(lbl.substring(0, 17), startX + 1.5, ry + 3);
      doc.setFont('helvetica', 'bold');
      // Número alineado a la derecha dentro de la col
      doc.text(val.toFixed(2), startX + numX, ry + 3, { align: 'right' });
    });

    // DESCUENTOS (col 1)
    descuentos.forEach(([lbl, val], i) => {
      const ry = y + 2 + i * rowH3;
      const cx = startX + col3;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...BLACK);
      doc.text(lbl.substring(0, 17), cx + 1.5, ry + 3);
      doc.setFont('helvetica', 'bold');
      doc.text(val.toFixed(2), cx + numX, ry + 3, { align: 'right' });
    });

    // APORTES (col 2)
    aportes.forEach(([lbl, val], i) => {
      const ry = y + 2 + i * rowH3;
      const cx = startX + col3 * 2;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...BLACK);
      doc.text(lbl.substring(0, 17), cx + 1.5, ry + 3);
      doc.setFont('helvetica', 'bold');
      doc.text(val.toFixed(2), cx + numX, ry + 3, { align: 'right' });
    });

    y += bodyH;

    // ── TOTALES ─────────────────────────────────────────────────────────────
    const totalDesc    = descuentos.reduce((s, [, v]) => s + v, 0);
    const totalAportes = aportes.reduce((s, [, v]) => s + v, 0);

    doc.setFillColor(...GRAY);
    doc.setDrawColor(...BORDER);
    [0, 1, 2].forEach(ci => {
      doc.rect(startX + ci * col3, y, col3, 6, 'FD');
    });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(...BLACK);

    // Col 0 totales
    doc.text('TOTAL INGRESOS', startX + 1.5, y + 4.2);
    doc.text(`S/${(item.gross_salary || 0).toFixed(2)}`, startX + numX, y + 4.2, { align: 'right' });

    // Col 1 totales
    const cx1 = startX + col3;
    doc.text('TOTAL DESCUENTOS', cx1 + 1.5, y + 4.2);
    doc.text(`S/${totalDesc.toFixed(2)}`, cx1 + numX, y + 4.2, { align: 'right' });

    // Col 2 totales
    const cx2 = startX + col3 * 2;
    doc.text('TOTAL APORTES', cx2 + 1.5, y + 4.2);
    doc.text(`S/${totalAportes.toFixed(2)}`, cx2 + numX, y + 4.2, { align: 'right' });

    y += 6;

    // ── NETO A PAGAR ─────────────────────────────────────────────────────────
    doc.setFillColor(...WHITE);
    doc.setDrawColor(...BORDER);
    doc.rect(startX, y, bolW, 7, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...BLACK);
    doc.text('NETO A PAGAR', startX + 2, y + 4.8);
    doc.text(`S/ ${(item.net_salary || 0).toFixed(2)}`, startX + bolW - 2, y + 4.8, { align: 'right' });
    y += 7;

    // ── FIRMAS ───────────────────────────────────────────────────────────────
    y += 8;
    const sigW = bolW / 2 - 6;
    doc.setDrawColor(...BORDER);
    doc.line(startX + 3,             y, startX + 3 + sigW,  y);
    doc.line(startX + bolW / 2 + 3,  y, startX + bolW - 3,  y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text('FIRMA DEL EMPLEADOR',   startX + 3 + sigW / 2,            y + 4.5, { align: 'center' });
    doc.text('FIRMA DEL TRABAJADOR',  startX + bolW / 2 + 3 + sigW / 2, y + 4.5, { align: 'center' });

    return y + 6;
  }


  // ── Exportar boleta individual (A4 horizontal, dos boletas lado a lado) ──
  function exportItemPDF(item: PayrollItem) {
    const emp = (item as any).employee;
    // Landscape A4: 297mm ancho × 210mm alto
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW  = doc.internal.pageSize.getWidth();  // 297mm
    const pageH  = doc.internal.pageSize.getHeight(); // 210mm
    const margin = 8;
    // Cada boleta ocupa la mitad del ancho menos márgenes: (297 - 8*3) / 2 ≈ 136mm
    const bolW = (pageW - margin * 3) / 2;

    // Boleta izquierda (ORIGINAL)
    drawBoleta(doc, item, margin, margin, bolW);

    // Línea divisoria punteada vertical central
    doc.setDrawColor(180, 185, 200);
    doc.setLineDashPattern([2, 2], 0);
    doc.line(margin * 2 + bolW, margin, margin * 2 + bolW, pageH - margin);
    doc.setLineDashPattern([], 0);

    // Boleta derecha (COPIA DEL TRABAJADOR)
    drawBoleta(doc, item, margin * 2 + bolW, margin, bolW);

    const lastName = (emp?.last_name || '').replace(/\s/g, '_');
    doc.save(`Boleta_${lastName}_${getMonthName(payroll!.period_month)}_${payroll!.period_year}.pdf`);
  }

  // ── Exportar TODAS las boletas (A4 horizontal, una por página) ───────────
  async function exportAllBoletas() {
    setExportingAll(true);
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW  = doc.internal.pageSize.getWidth();
    const pageH  = doc.internal.pageSize.getHeight();
    const margin = 8;
    const bolW   = (pageW - margin * 3) / 2;

    items.forEach((item, idx) => {
      if (idx > 0) doc.addPage('a4', 'landscape');
      drawBoleta(doc, item, margin, margin, bolW);
      doc.setDrawColor(180, 185, 200);
      doc.setLineDashPattern([2, 2], 0);
      doc.line(margin * 2 + bolW, margin, margin * 2 + bolW, pageH - margin);
      doc.setLineDashPattern([], 0);
      drawBoleta(doc, item, margin * 2 + bolW, margin, bolW);
    });

    doc.save(`Boletas_${getMonthName(payroll!.period_month)}_${payroll!.period_year}.pdf`);
    setExportingAll(false);
  }

  // ── Exportar planilla resumen formato tabla SUNAT ─────────────────────────
  async function exportFullPayrollPDF() {
    setExporting(true);
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 10;

    // Header
    doc.setFillColor(0, 61, 155);
    doc.rect(0, 0, pageW, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('PLANILLA DE REMUNERACIONES', margin, 9);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.text(`${(company?.name || '').toUpperCase()}  ·  RUC: ${company?.ruc || ''}`, margin, 14);
    doc.text(
      `Período: ${getMonthName(payroll!.period_month).toUpperCase()} ${payroll!.period_year}  ·  ${getRegimeName(company?.regime || 'general')}`,
      pageW - margin, 14, { align: 'right' }
    );

    const tableBody = items.map((item, i) => {
      const emp = (item as any).employee;
      const afpAmt = (item.onp_amount || 0) + (item.afp_amount || 0);
      return [
        i + 1,
        `${(emp?.last_name || '').toUpperCase()}, ${(emp?.first_name || '').toUpperCase()}`,
        emp?.dni || '',
        emp?.position || '',
        (item.base_salary || 0).toFixed(2),
        (item.family_assignment || 0).toFixed(2),
        (item.gratification || 0).toFixed(2),
        (item.other_income || 0).toFixed(2),
        (item.overtime_amount || 0).toFixed(2),
        (item.bonuses_total || 0).toFixed(2),
        (item.gross_salary || 0).toFixed(2),
        afpAmt.toFixed(2),
        (item.income_tax || 0).toFixed(2),
        ((item.absence_deduction || 0) + (item.tardiness_deduction || 0)).toFixed(2),
        (item.total_deductions || 0).toFixed(2),
        (item.net_salary || 0).toFixed(2),
        (item.essalud_amount || 0).toFixed(2),
        (item.cts_provision || 0).toFixed(2),
      ];
    });

    // Fila totales
    const sum = (f: keyof PayrollItem) => items.reduce((s, i) => s + (Number((i as any)[f]) || 0), 0);
    tableBody.push([
      '', 'T O T A L E S', '', '',
      sum('base_salary').toFixed(2),
      sum('family_assignment').toFixed(2),
      sum('gratification').toFixed(2),
      sum('other_income').toFixed(2),
      sum('overtime_amount').toFixed(2),
      sum('bonuses_total').toFixed(2),
      sum('gross_salary').toFixed(2),
      (sum('onp_amount') + sum('afp_amount')).toFixed(2),
      sum('income_tax').toFixed(2),
      (sum('absence_deduction') + sum('tardiness_deduction')).toFixed(2),
      sum('total_deductions').toFixed(2),
      sum('net_salary').toFixed(2),
      sum('essalud_amount').toFixed(2),
      sum('cts_provision').toFixed(2),
    ]);

    autoTable(doc, {
      startY: 24,
      head: [[
        'N°', 'APELLIDOS Y NOMBRES', 'DNI', 'CARGO',
        'REM.\nBÁSICA', 'ASIG.\nFAM.', 'GRATIF.', 'BONIF.\nEXT.', 'H.EXT.', 'BONOS',
        'TOTAL\nBRUTO',
        'ONP/AFP', 'RENTA\n5TA', 'DESC.\nASIST.',
        'TOTAL\nDESC.',
        'NETO\nPAGAR',
        'ESSALUD\n(Empl.)', 'CTS\nPROV.',
      ]],
      body: tableBody,
      theme: 'grid',
      styles: { fontSize: 6.5, cellPadding: 1.5, halign: 'right', textColor: [25, 28, 30], font: 'helvetica' },
      headStyles: {
        fillColor: [0, 61, 155], textColor: 255, fontStyle: 'bold',
        fontSize: 6.5, halign: 'center', valign: 'middle', minCellHeight: 8,
      },
      columnStyles: {
        0: { cellWidth: 6, halign: 'center' },
        1: { cellWidth: 40, halign: 'left' },
        2: { cellWidth: 14 },
        3: { cellWidth: 20, halign: 'left' },
      },
      margin: { left: margin, right: margin },
      didParseCell: (data) => {
        if (data.row.index === tableBody.length - 1) {
          data.cell.styles.fillColor = [220, 230, 255];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    const finalY = (doc as any).lastAutoTable.finalY + 8;

    // Resumen
    doc.setFillColor(0, 61, 155);
    doc.roundedRect(margin, finalY, 100, 16, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text('RESUMEN', margin + 4, finalY + 5.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(`Total Bruto: S/ ${sum('gross_salary').toFixed(2)}`, margin + 4, finalY + 10);
    doc.text(`Total Descuentos: S/ ${sum('total_deductions').toFixed(2)}`, margin + 54, finalY + 10);

    doc.setFillColor(0, 82, 204);
    doc.roundedRect(margin + 104, finalY, 70, 16, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('TOTAL NETO A PAGAR:', margin + 108, finalY + 6);
    doc.setFontSize(10);
    doc.text(`S/ ${sum('net_salary').toFixed(2)}`, margin + 108, finalY + 13);

    // Firmas
    const sigY = finalY + 28;
    const sigSpacing = (pageW - margin * 2) / 3;
    ['Elaborado por', 'Revisado por RR.HH.', 'Autorizado por Gerencia'].forEach((label, i) => {
      const x = margin + i * sigSpacing + sigSpacing * 0.1;
      doc.setTextColor(115, 118, 133);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setDrawColor(180, 185, 200);
      doc.line(x, sigY, x + sigSpacing * 0.8, sigY);
      doc.text(label, x + sigSpacing * 0.4, sigY + 4.5, { align: 'center' });
    });

    doc.setFontSize(6);
    doc.setTextColor(180, 180, 180);
    doc.text(
      `Generado el ${new Date().toLocaleString('es-PE')} · ${company?.name} · RUC ${company?.ruc} · RRHH Up Solutions`,
      pageW / 2,
      doc.internal.pageSize.getHeight() - 5,
      { align: 'center' }
    );

    doc.save(`Planilla_${getMonthName(payroll!.period_month)}_${payroll!.period_year}.pdf`);
    setExporting(false);
  }

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════
  if (loading) return (
    <div className="p-12 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!payroll) return (
    <div className="p-12 text-center text-on-surface-variant">Planilla no encontrada</div>
  );

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Breadcrumb */}
      <div>
        <button onClick={() => navigate('/payroll')} className="flex items-center gap-1 text-on-surface-variant text-sm hover:text-primary transition-colors mb-4">
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Volver a planillas
        </button>
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight text-on-surface font-headline">
              Planilla — {getMonthName(payroll.period_month)} {payroll.period_year}
            </h1>
            <p className="text-on-surface-variant mt-1">
              {items.length} empleados · {getRegimeName(company?.regime || 'general')}
            </p>
          </div>
          {/* Botones de exportación */}
          <div className="flex gap-3">
            <button
              onClick={exportAllBoletas}
              disabled={exportingAll}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-primary text-primary font-bold text-sm hover:bg-primary/5 active:scale-[0.98] transition-all disabled:opacity-60"
            >
              <span className="material-symbols-outlined text-[18px]">picture_as_pdf</span>
              {exportingAll ? 'Generando...' : 'Todas las boletas'}
            </button>
            <button
              onClick={exportFullPayrollPDF}
              disabled={exporting}
              className="btn-primary text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-primary/20 flex items-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60"
            >
              <span className="material-symbols-outlined text-[18px]">download</span>
              {exporting ? 'Generando PDF...' : 'Planilla completa PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-5">
        {[
          { label: 'Total Bruto', value: formatCurrency(payroll.total_gross), color: 'text-on-surface', bg: '' },
          { label: 'Deducciones', value: formatCurrency(payroll.total_deductions), color: 'text-error', bg: 'bg-error-container/20' },
          { label: 'EsSalud (Empleador)', value: formatCurrency(payroll.total_essalud), color: 'text-secondary', bg: '' },
          { label: 'Neto a Pagar', value: formatCurrency(payroll.total_net), color: 'text-primary', bg: 'bg-primary/5' },
        ].map(s => (
          <div key={s.label} className={`card p-5 ${s.bg}`}>
            <p className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest mb-2">{s.label}</p>
            <p className={`text-2xl font-black tabular-nums ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Nota sobre boletas */}
      <div className="flex items-start gap-3 p-4 bg-primary/5 rounded-xl border border-primary/15">
        <span className="material-symbols-outlined text-primary text-[20px] mt-0.5">info</span>
        <div className="text-sm">
          <p className="font-bold text-on-surface">Formato boleta SUNAT</p>
          <p className="text-on-surface-variant mt-0.5">
            Cada boleta se genera en hoja A4 con <strong>dos copias</strong> (original + copia del trabajador), 
            siguiendo el formato oficial peruano: datos del trabajador, datos laborales, 
            tabla ingresos/descuentos/aportes, neto a pagar y firmas.
          </p>
        </div>
      </div>

      {/* Items table */}
      <div className="card overflow-hidden">
        <div className="p-5 border-b border-outline-variant/10 flex items-center justify-between">
          <h3 className="font-bold text-on-surface font-headline">Detalle por empleado</h3>
          <span className="text-xs text-on-surface-variant">Clic en "Boleta" para descargar boleta individual en formato SUNAT</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-surface-container-low">
                {['Empleado', 'Cargo', 'Días', 'Bruto', 'ONP/AFP', 'Renta 5ta', 'Desc. Asist.', 'Neto', 'EsSalud', ''].map(h => (
                  <th key={h} className="px-5 py-3 text-[10px] font-bold text-on-surface-variant uppercase tracking-widest whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10">
              {items.map(item => {
                const emp = (item as any).employee;
                const afpAmt = (item.onp_amount || 0) + (item.afp_amount || 0);
                const asistDesc = (item.absence_deduction || 0) + (item.tardiness_deduction || 0);
                return (
                  <tr key={item.id} className="hover:bg-surface-container-low/30 transition-colors group">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs flex-shrink-0">
                          {emp?.first_name?.[0]}{emp?.last_name?.[0]}
                        </div>
                        <span className="font-bold text-on-surface text-xs">{emp?.last_name}, {emp?.first_name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-on-surface-variant text-xs max-w-[100px] truncate">{emp?.position}</td>
                    <td className="px-5 py-4 text-xs font-medium tabular-nums">
                      <span className={item.days_worked < item.working_days ? 'text-amber-600 font-bold' : 'text-on-surface'}>
                        {item.days_worked}/{item.working_days}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-semibold tabular-nums text-xs">{formatCurrency(item.gross_salary)}</td>
                    <td className="px-5 py-4 text-error tabular-nums text-xs">{afpAmt > 0 ? `(${formatCurrency(afpAmt)})` : '—'}</td>
                    <td className="px-5 py-4 text-error tabular-nums text-xs">{(item.income_tax || 0) > 0 ? `(${formatCurrency(item.income_tax)})` : '—'}</td>
                    <td className="px-5 py-4 tabular-nums text-xs">
                      {asistDesc > 0 ? <span className="text-amber-600 font-bold">({formatCurrency(asistDesc)})</span> : '—'}
                    </td>
                    <td className="px-5 py-4 font-black text-primary tabular-nums text-sm">{formatCurrency(item.net_salary)}</td>
                    <td className="px-5 py-4 text-on-surface-variant tabular-nums text-xs">{formatCurrency(item.essalud_amount)}</td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => exportItemPDF(item)}
                        title="Descargar boleta individual formato SUNAT"
                        className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-xs font-bold text-primary bg-primary/10 hover:bg-primary/20 px-3 py-1.5 rounded-lg whitespace-nowrap"
                      >
                        <span className="material-symbols-outlined text-[14px]">receipt</span>
                        Boleta PDF
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
